import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/store/auth.store';
import { hasAdminPortalAccess, hasRequiredRole, OWNER_LEVEL_ROLES, ADMIN_PORTAL_ROLES } from '@/constants/roles';
import { applyPageSeo, PageSeoMeta } from '@/utils/seo';
import HomeView from '../views/HomeView.vue';
import PublicBookingView from '../views/PublicBookingView.vue';
import PublicAcademyEnquiryView from '../views/PublicAcademyEnquiryView.vue';
import BookingSuccessView from '../views/BookingSuccessView.vue';
import AdminLayout from '@/layouts/AdminLayout.vue';
const AdminAppointmentManagementView = () => import(/* webpackChunkName: "admin-appointments" */ '@/views/admin/AdminAppointmentManagementView.vue');
const AdminCustomerManagementView = () => import(/* webpackChunkName: "admin-customers" */ '@/views/admin/AdminCustomerManagementView.vue');
const AdminServiceManagementView = () => import(/* webpackChunkName: "admin-services" */ '@/views/admin/AdminServiceManagementView.vue');
const AdminEmployeeManagementView = () => import(/* webpackChunkName: "admin-employees" */ '@/views/admin/AdminEmployeeManagementView.vue');
const AdminOfferManagementView = () => import(/* webpackChunkName: "admin-offers" */ '@/views/admin/AdminOfferManagementView.vue');
const AdminSpinCampaignManagementView = () => import(/* webpackChunkName: "admin-spin-campaigns" */ '@/views/admin/AdminSpinCampaignManagementView.vue');
const AdminGalleryManagementView = () => import(/* webpackChunkName: "admin-gallery" */ '@/views/admin/AdminGalleryManagementView.vue');
const AdminReportsView = () => import(/* webpackChunkName: "admin-reports" */ '@/views/admin/AdminReportsView.vue');
const AdminAppointmentEditView = () => import(/* webpackChunkName: "admin-appointment-edit" */ '@/views/admin/AdminAppointmentEditView.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      title: 'Attractive Unisex Salon & Academy | Hair, Beauty & Bridal – Gondia',
      description:
        'Attractive Unisex Salon & Academy in Gondia. Expert haircuts, colour, skincare, bridal makeup, and beauty training near Ayush Hospital, Rail Toly. Book online today.',
    },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    meta: {
      title: 'About Attractive Salon & Academy – Gondia',
      description:
        'Learn about Attractive Salon & Academy in Gondia — a professional unisex salon and hands-on beauty academy helping you look better and grow with confidence.',
    },
  },
  {
    path: '/services',
    name: 'services',
    component: () => import(/* webpackChunkName: "services" */ '../views/PublicServicesView.vue'),
    meta: {
      title: 'Salon Services in Gondia – Hair, Skin & Bridal',
      description:
        'Explore hair styling, colour, skincare, bridal makeup, and more at Attractive Unisex Salon in Gondia. View our full range of professional beauty services.',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../views/LoginView.vue'),
    meta: { title: 'Staff Login', noIndex: true },
  },
  {
    path: '/book-appointment',
    name: 'PublicBooking',
    component: PublicBookingView,
    meta: {
      title: 'Book an Appointment – Attractive Salon Gondia',
      description:
        'Book your appointment online at Attractive Unisex Salon in Gondia. Choose your service and preferred time in a few simple steps.',
    },
  },
  {
    path: '/academy-enquiry',
    name: 'AcademyEnquiry',
    component: PublicAcademyEnquiryView,
    meta: {
      title: 'Beauty Academy Enquiry – Attractive Salon Gondia',
      description:
        'Enquire about beauty and salon training at Attractive Salon & Academy in Gondia. Hands-on courses in a live salon environment.',
    },
  },
  {
    path: '/spin/:slug',
    name: 'PublicSpin',
    component: () => import(/* webpackChunkName: "public-spin" */ '../views/PublicSpinView.vue'),
    props: true,
    meta: {
      title: 'Spin & Win – Attractive Salon Gondia',
      description: 'Spin the Attractive Salon prize wheel and unlock a limited-time voucher for your next visit.',
      noIndex: true,
    },
  },
  {
    path: '/share-your-style',
    name: 'ShareYourStyle',
    component: () => import(/* webpackChunkName: "upload" */ '../views/UploadImageView.vue'),
    meta: {
      title: 'Share Your Style – Attractive Salon',
      description: 'Share your look with Attractive Unisex Salon in Gondia and inspire our community.',
    },
  },
  {
    path: '/booking-success/:id',
    name: 'BookingSuccess',
    component: BookingSuccessView,
    props: true,
    meta: { title: 'Booking Confirmed', noIndex: true },
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, requiresAdmin: true, noIndex: true, title: 'Admin' },
    children: [
      {
        path: '',
        redirect: '/admin/appointments',
      },
      {
        path: 'appointments',
        name: 'AdminAppointments',
        component: AdminAppointmentManagementView,
        meta: { title: 'Appointments', requiredRoles: [...ADMIN_PORTAL_ROLES], noIndex: true },
      },
      {
        path: 'appointments/:id/edit',
        name: 'AdminAppointmentEdit',
        component: AdminAppointmentEditView,
        meta: { title: 'Edit Appointment', requiredRoles: [...ADMIN_PORTAL_ROLES], noIndex: true },
        props: true,
      },
      {
        path: 'customers',
        name: 'AdminCustomers',
        component: AdminCustomerManagementView,
        meta: { title: 'Customers', requiredRoles: [...ADMIN_PORTAL_ROLES], noIndex: true },
      },
      {
        path: 'services',
        name: 'AdminServices',
        component: AdminServiceManagementView,
        meta: { title: 'Services', requiredRoles: [...OWNER_LEVEL_ROLES], noIndex: true },
      },
      {
        path: 'employees',
        name: 'AdminEmployees',
        component: AdminEmployeeManagementView,
        meta: { title: 'Employees', requiredRoles: [...OWNER_LEVEL_ROLES], noIndex: true },
      },
      {
        path: 'offers',
        name: 'AdminOffers',
        component: AdminOfferManagementView,
        meta: { title: 'Offers', requiredRoles: [...OWNER_LEVEL_ROLES], noIndex: true },
      },
      {
        path: 'spin-campaigns',
        name: 'AdminSpinCampaigns',
        component: AdminSpinCampaignManagementView,
        meta: { title: 'Spin Campaigns', requiredRoles: [...OWNER_LEVEL_ROLES], noIndex: true },
      },
      {
        path: 'gallery',
        name: 'AdminGallery',
        component: AdminGalleryManagementView,
        meta: { title: 'Gallery', requiredRoles: [...ADMIN_PORTAL_ROLES], noIndex: true },
      },
      {
        path: 'reports',
        name: 'AdminReports',
        component: AdminReportsView,
        meta: { title: 'Reports', requiredRoles: [...OWNER_LEVEL_ROLES], noIndex: true },
      },
    ],
  },
  {
    path: '/:catchAll(.*)*',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "notfound" */ '../views/NotFoundView.vue'),
    meta: { title: 'Page Not Found', noIndex: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const isAuthenticated = authStore.isUserLoggedIn;
  const userRole = authStore.user?.role?.name;

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);

  if (requiresAdmin) {
    if (!isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }
    if (!hasAdminPortalAccess(userRole)) {
      next({ name: 'home' });
      return;
    }

    const requiredRoles = to.matched
      .map((record) => record.meta.requiredRoles as readonly string[] | undefined)
      .find((roles) => roles && roles.length > 0);

    if (requiredRoles && !hasRequiredRole(userRole, requiredRoles)) {
      next({ name: 'AdminAppointments' });
      return;
    }

    next();
    return;
  }

  if (requiresAuth) {
    if (!isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }
    next();
    return;
  }

  next();
});

router.afterEach((to) => {
  const matchedWithSeo = [...to.matched].reverse().find((record) => record.meta?.title || record.meta?.description);
  const seo: PageSeoMeta = {
    title: matchedWithSeo?.meta?.title as string | undefined,
    description: matchedWithSeo?.meta?.description as string | undefined,
    noIndex: to.matched.some((record) => record.meta?.noIndex),
  };
  applyPageSeo(seo, to.path);
});

export default router;
